import React from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Mail } from "lucide-react";

/**
 * Shared "Contact us" dialog. Wired to POST /api/contact which delivers to
 * info@dividendportfolioplanner.com via Resend (with the sender as Reply-To).
 *
 * Reused by:
 *  - Pricing page (opened by pricing-contact-open pill)
 *  - Layout desktop nav (nav-contact-us)
 *  - WhyDpp page (two entry points: before FAQ list + after CTA band)
 *
 * Pre-fills Name/Email from the logged-in user when available.
 */
export default function ContactDialog({ open, onOpenChange, defaultName = "", defaultEmail = "", chatTranscript = null, onSubmitted = null }) {
  const [name, setName] = React.useState(defaultName);
  const [email, setEmail] = React.useState(defaultEmail);
  const [message, setMessage] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setEmail(defaultEmail || "");
      // Prefill the message textarea with the last user turn from the chat so
      // they don't retype their original question.
      if (chatTranscript && chatTranscript.length) {
        const lastUser = [...chatTranscript].reverse().find((t) => t.role === "user");
        setMessage(lastUser ? `Follow-up from my chat with the DPP Assistant:\n\n${lastUser.content}\n` : "");
      } else {
        setMessage("");
      }
    }
  }, [open, defaultName, defaultEmail, chatTranscript]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const n = name.trim();
    const em = email.trim();
    const m = message.trim();
    if (!n) { toast.error("Please tell us your name"); return; }
    if (!em || !em.includes("@")) { toast.error("Please enter a valid email"); return; }
    if (m.length < 5) { toast.error("Please add a short message"); return; }
    setSubmitting(true);
    try {
      const body = { name: n, email: em, message: m };
      if (chatTranscript && chatTranscript.length) body.chat_transcript = chatTranscript;
      await api.post("/contact", body);
      toast.success("Thanks! We'll get back to you shortly.");
      onSubmitted ? onSubmitted() : onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send — please email info@dividendportfolioplanner.com");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Contact us</DialogTitle>
          <DialogDescription>
            Have a question about pricing, features, or an integration you&apos;d like? Send us a note — we read every one and reply within one business day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="contact-dialog-name">Name</Label>
            <Input
              id="contact-dialog-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Investor"
              autoComplete="name"
              maxLength={120}
              required
              data-testid="contact-dialog-name"
            />
          </div>
          <div>
            <Label htmlFor="contact-dialog-email">Email</Label>
            <Input
              id="contact-dialog-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              data-testid="contact-dialog-email"
            />
          </div>
          <div>
            <Label htmlFor="contact-dialog-message">Message</Label>
            <Textarea
              id="contact-dialog-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Tell us what you're looking for…"
              maxLength={4000}
              required
              data-testid="contact-dialog-message"
            />
            <div className="mono text-[10px] text-muted-foreground text-right mt-1">
              {message.length}/4000
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="contact-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full"
              data-testid="contact-dialog-submit"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                : <><Mail className="w-4 h-4 mr-2" /> Send message</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
